import { TitleBlockAddBorders } from "./TitleBlockAddBorders";
import debug from "debug";
const log = debug("app:image");

/**
 * Manages the creation, updating, and removal of title block borders.
 * @category Components
 * @subcategory TitleBlock
 */
export class TitleBlockBorders {
  /**
   * Constructs a new TitleBlockBorders instance.
   * @param {Object} experience - The experience object containing shared application resources.
   */
  constructor(experience) {
    this.eventHub = experience.eventHub;
    this.borderContainer = null;

    this.init();
  }

  /**
   * Initializes the TitleBlockBorders class.
   */
  init() {
    log("Initializing TitleBlockBorders");
  }


  cleanup() {
    log("Cleaning up TitleBlockBorders...");
  
    // Remove border container if it exists
    if (this.borderContainer) {
      this.borderContainer.remove();
      this.borderContainer = null;
    }
  
    // Cleanup reference to the TitleBlockAddBorders instance
    if (this.titleBlockAddBorders) {
      this.titleBlockAddBorders.cleanup(); // Ensure child class cleanup if implemented
      this.titleBlockAddBorders = null;
    }
  
    // Reset eventHub to avoid memory leaks
    this.eventHub = null;
  
    log("TitleBlockBorders cleanup completed.");
  }

  // Create borders with initial sizes

  /**
   * Creates borders for the title block based on the provided sizes.
   * @param {Object} sizes - The dimensions of the title block container.
   * @param {number} sizes.width - The width of the container.
   * @param {number} sizes.height - The height of the container.
   * @param {number} sizes.top - The top offset of the container.
   */
  createBorders(sizes) {
    log("sizes", sizes);
    if (!this.borderContainer) {
      const { width, height, top } = sizes;

      this.borderContainer = document.createElement("div");
      this.borderContainer.className = "border-container";
      this.borderContainer.style.position = "fixed";
      this.borderContainer.style.top = `${top}px`;
      this.borderContainer.style.left = "0";
      this.borderContainer.style.width = `${width}px`;
      this.borderContainer.style.height = `${height}px`;
      this.borderContainer.style.backgroundColor = "transparent";
      this.borderContainer.style.zIndex = "100";
      this.borderContainer.style.pointerEvents = "none";
      this.titleBlockAddBorders = new TitleBlockAddBorders(this.borderContainer);
      this.titleBlockAddBorders.addBorders();
      document.body.appendChild(this.borderContainer);
    }

    this._showBorders();
  }

  // Updates the borders when the sizes change dynamically

  /**
   * Updates the borders dynamically based on the provided sizes.
   * @param {Object} sizes - The new dimensions of the title block container.
   * @param {number} sizes.width - The new width of the container.
   * @param {number} sizes.height - The new height of the container.
   * @param {number} sizes.top - The new top offset of the container.
   */
  updateBorders(sizes) {
    if (this.borderContainer) {
      const { width, height, top } = sizes;
      this.borderContainer.style.top = `${top}px`;
      this.borderContainer.style.width = `${width}px`;
      this.borderContainer.style.height = `${height}px`;
    }
  }

  // Helper method to create borders with specific position and styles


  /**
   * Helper method to create a border element with specific position and styles.
   * @param {string} position - The position of the border (e.g., "top", "left").
   * @param {string} top - The top offset for the border.
   * @param {string} left - The left offset for the border.
   * @param {string} width - The width of the border.
   * @param {string} height - The height of the border.
   * @param {string} borderProperty - The CSS property for the border (e.g., "borderTop").
   * @param {number} lineWidth - The width of the border line.
   */
  _createBorder(position, top, left, width, height, borderProperty, lineWidth) {
    const border = document.createElement("div");
    border.style.position = "absolute";
    border.style.top = top;
    border.style.left = left;
    border.style.width = width;
    border.style.height = height;
    border.style[borderProperty] = `${lineWidth}px solid black`; // Set border based on property
    this.borderContainer.appendChild(border);
  }
  
  /**
   * Helper method to create a text element at a specific position.
   * @param {string} content - The text content to display.
   * @param {string} top - The top offset for the text.
   * @param {string} left - The left offset for the text.
   * @param {string} fontSize - The font size for the text.
   * @param {string} align - The text alignment (e.g., "left", "center").
   */
  _createText(content, top, left, fontSize, align) {
    const textElement = document.createElement("div");
    textElement.style.position = "absolute";
    textElement.style.top = top;
    textElement.style.left = left;
    textElement.style.fontSize = fontSize;
    textElement.style.textAlign = align;
    textElement.innerText = content;
    this.borderContainer.appendChild(textElement);
}

  /**
   * Displays the borders by setting the container's display to "block".
   */
  _showBorders() {
    if (this.borderContainer) {
      this.borderContainer.style.display = "block";
    }
  }

  /*   hideBorders() {
    if (this.borderContainer) {
      this.borderContainer.style.display = "none";
    }
  } */


  /**
   * Removes the borders and cleans up the container.
   */
  removeBorders() {
    if (this.borderContainer) {
      this.borderContainer.remove();
      this.borderContainer = null;
    }
  }
}
